import React from 'react';
import { useParams, Link } from 'react-router-dom';

const portfolioData = {
  1: {
    title: 'Enterprise Resource Planning Portal',
    type: 'Web Platform',
    image: '/images/portfolio-7.webp',
    client: 'Confidential (B2B)',
    year: '2024',
    description: [
      'We built a custom ERP portal for a mid-sized manufacturer to manage inventory, orders, and reporting in one place. The previous setup relied on spreadsheets and multiple legacy systems; the new portal integrates with their existing ERP and provides role-based dashboards and workflows.',
      'The system was built with React and Node.js, deployed on AWS, and includes SSO, audit logging, and automated reporting. Delivery was in four phases over six months, with training and documentation handoff.'
    ],
    tech: ['React', 'Node.js', 'AWS', 'PostgreSQL', 'SSO'],
    outcomes: ['Unified view of inventory and orders', 'Faster approval workflows', 'Automated reporting and exports']
  },
  2: {
    title: 'Field Service Management App',
    type: 'Mobile App',
    image: '/images/portfolio-8.webp',
    client: 'Confidential (Field Services)',
    year: '2024',
    description: [
      'A cross-platform mobile app for field technicians to receive work orders, update job status, capture signatures and photos, and sync data in near real time. The app works offline when connectivity is poor and queues updates until back online.',
      'Built with Flutter for iOS and Android, backed by REST APIs and Firebase for push and sync. The client saw a significant drop in admin follow-ups and faster billing cycles.'
    ],
    tech: ['Flutter', 'Firebase', 'REST API', 'Offline-first'],
    outcomes: ['Real-time job updates from the field', 'Fewer manual data-entry errors', 'Faster invoicing and close-out']
  },
  3: {
    title: 'Legacy System Cloud Migration',
    type: 'Cloud Migration',
    image: '/images/portfolio-9.webp',
    client: 'Confidential (Enterprise)',
    year: '2023',
    description: [
      'We migrated a set of on-premises .NET and SQL Server workloads to Azure. The project included refactoring some components for cloud-native patterns, introducing infrastructure-as-code with Terraform, and setting up CI/CD and monitoring.',
      'The migration was done in stages with zero-downtime cutover. We left the client with runbooks, cost dashboards, and a clear path for future scaling.'
    ],
    tech: ['Azure', 'Terraform', 'Azure DevOps', '.NET Core'],
    outcomes: ['Reduced on-prem footprint and cost', 'Automated deployments and rollback', 'Improved uptime and visibility']
  },
  4: {
    title: 'Healthcare Analytics Dashboard',
    type: 'UI/UX',
    image: '/images/portfolio-10.webp',
    client: 'Confidential (Healthcare)',
    year: '2024',
    description: [
      'A real-time analytics dashboard for clinicians and administrators at a regional health network. The dashboard shows patient flow, acuity, staffing, and key quality metrics—all with role-based access and audit trails.',
      'We designed the UX in Figma with input from nurses and admins, then built the front end in React with a FHIR-compliant backend. The system integrates with their EHR and respects HIPAA and accessibility requirements.'
    ],
    tech: ['Figma', 'React', 'FHIR', 'Role-based access'],
    outcomes: ['Single view of operations and quality', 'Faster decision-making for care teams', 'Compliance-ready access and audit']
  },
  5: {
    title: 'B2B E-commerce Platform',
    type: 'E-commerce',
    image: '/images/portfolio-11.webp',
    client: 'Confidential (Distribution)',
    year: '2023',
    description: [
      'A B2B ordering platform for a distributor with custom pricing, approval workflows, and integration to their existing ERP and CRM. Customers can browse catalogs, place orders, track shipments, and manage returns from one portal.',
      'Built with React and Node.js, Stripe for payments, and REST APIs to the client’s back-office systems. We delivered in phases: MVP ordering, then approvals, then advanced reporting and integrations.'
    ],
    tech: ['React', 'Node.js', 'Stripe', 'ERP/CRM integration'],
    outcomes: ['Self-service ordering and tracking', 'Approval flows and spend controls', 'Accurate sync with ERP and CRM']
  },
  6: {
    title: 'Banking & Payments Mobile App',
    type: 'Fintech',
    image: '/images/portfolio-12.webp',
    client: 'Confidential (Financial Services)',
    year: '2024',
    description: [
      'A secure mobile banking app with balance checks, transfers, bill pay, and card controls. The app supports biometric login, push notifications for transactions, and meets regulatory expectations for identity and data protection.',
      'We delivered native iOS (Swift) and Android (Kotlin) clients, plus API design and integration with the client’s core banking and identity providers. Security review and penetration testing were part of the engagement.'
    ],
    tech: ['Swift', 'Kotlin', 'OAuth', 'Biometrics', 'REST APIs'],
    outcomes: ['Secure, compliant mobile banking', 'Biometric and push-based UX', 'Ready for audit and scaling']
  }
};

const PortfolioDetails = () => {
  const { id } = useParams();
  const project = portfolioData[id];

  if (!project) {
    return (
      <div className="portfolio-details section">
        <div className="container">
          <p>Project not found.</p>
          <Link to="/portfolio">Back to Portfolio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="portfolio-details section">
      <div className="container">
        <div className="row">
          <div className="col-lg-8">
            <div className="portfolio-details-content">
              <div className="portfolio-details-header mb-4">
                <span className="project-type-badge">{project.type}</span>
                <h2 className="project-title">{project.title}</h2>
                <div className="project-meta">
                  <span><strong>Client:</strong> {project.client}</span>
                  <span><strong>Year:</strong> {project.year}</span>
                </div>
              </div>
              <div className="portfolio-details-image mb-4">
                <img src={project.image} alt={project.title} className="img-fluid rounded" />
              </div>
              {project.description.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
              <h4 className="mt-4">Technologies</h4>
              <div className="tech-badges mb-4">
                {project.tech.map((t, i) => (
                  <span key={i} className="tech-badge">{t}</span>
                ))}
              </div>
              <h4>Outcomes</h4>
              <ul className="project-outcomes">
                {project.outcomes.map((o, i) => (
                  <li key={i}>{o}</li>
                ))}
              </ul>
              <div className="mt-4">
                <Link to="/contact" className="btn btn-primary">Start Your Project</Link>
                <Link to="/portfolio" className="btn btn-outline ms-2">View All Work</Link>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="sidebar">
              <div className="project-info">
                <h4>Project Overview</h4>
                <ul className="project-facts">
                  <li>
                    <span className="fact-label">Category</span>
                    <span className="fact-value">{project.type}</span>
                  </li>
                  <li>
                    <span className="fact-label">Client</span>
                    <span className="fact-value">{project.client}</span>
                  </li>
                  <li>
                    <span className="fact-label">Year</span>
                    <span className="fact-value">{project.year}</span>
                  </li>
                </ul>
                <Link to="/contact" className="btn btn-primary w-100 mt-3">Discuss Your Project</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioDetails;
